// Ficha 04 — Forma de pago (el router). Look 1:1 con el mock `wizard/04-forma-pago.html`:
// wizard-card con head (eyebrow · "¿Cómo va a pagar?" · contexto del equipo elegido) y body con
// las `.pay-card` — SureKeep destacada (badge "Recomendada" si aplica) · Contado ·
// grid de "Otras financieras".
//
// INTEGRADA (guía §04): las opciones vienen de `GET …/financiers` (catálogo SOFOM × elegibilidad
// del producto). Selección en DOS fases como el mock (card → «Continuar»):
//   · SureKeep → POST …/payment-method (el backend crea el acreditado) → 05 Score.
//   · Contado  → POST …/payment-method → 07 Cobro.
//   · Externa  → modal → POST …/external-referral: sin extras pausa la venta y salta a la
//     Bienvenida (10); con extras la deja activa y lleva al cobro de contado de los extras.
// Card SureKeep: sin nivel previo (`session.loan.level`) muestra «Falta revisar su historial».
import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ArrowRight, CreditCard, ExternalLink, ShieldCheck } from 'lucide-react';
import { Button, ConfirmDialog, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { useCatalog, useFinanciers, useWizardSession } from '../api/useWizard';
import { usePaymentMethodChange, type PaymentTrack } from '../api/usePaymentMethodChange';
import { useReferralFlow, type AfterReferral } from '../api/useReferralFlow';
import { externalProviderName, isReferredWithExtras } from '../externalReferral';
import type { OnSaleChanged } from '../promotionSlots';
import { useCatalogLineInfo } from '../api/useCatalogLineInfo';
import { wizardErrorMsg } from '../api/wizardError';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { DroppedPromotionsNotice } from '../components/DroppedPromotionsNotice';
import { previousStepSlug } from '../steps';
import {
    catalogForSession,
    isSureKeepAvailable,
    sureKeepIneligibleCopy,
} from '../sureKeepAvailability';
import { InlineQueryError } from '../components/QueryErrorNotice';
import { cashChargeCents, financeableCartLinesOf } from '../saleCharge';
import { descriptionsBySku } from '../cartLines';
import {
    anchorLineOf,
    isAnchorUnfinanceable,
    kitPieceNamesOf,
    lineVariantsBySku,
    saleSummaryOf,
} from '../buildSale';
import { BuildSaleSummaryPanel } from '../components/BuildSaleSummary';
import { HowItWorksButton } from '../components/HowItWorksModal';
import { PAYMENT_METHOD_HOW_IT_WORKS } from '../components/creditStepsHowItWorks';
import type { LineSlots } from '../components/LineLabels';
import { sureKeepSplitOf } from '../sureKeepSplit';
import { ReferralExtrasNotice } from '../components/ReferralExtrasNotice';
import { CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE } from '../cashOnlyPayment';
import type { FinancierOption, WizardSession } from '../types';

/** `.pay-card` del mock: tarjeta seleccionable de 1.5px con ícono grande a la izquierda. */
const PAY_CARD =
    'relative flex w-full items-start gap-3.5 rounded-[10px] border-[1.5px] border-border bg-card px-4 py-3.5 text-left transition-colors';
/** `.pay-icon` — cuadro de 44px con el ícono de la opción. */
const PAY_ICON = 'flex size-11 flex-none items-center justify-center rounded-[10px]';
/** Botón que elige la opción dentro de su tarjeta; las promos van debajo, fuera del botón. */
const CARD_BUTTON = 'flex w-full items-start gap-3.5 text-left disabled:cursor-not-allowed';
/** Promos de la opción, alineadas con el texto (44px del ícono + 14px de espacio). */
const OPTION_PROMOS = 'pl-[58px]';
/** Caja de montos a la derecha de cada tarjeta. */
const AMOUNT_BOX = 'hidden w-[180px] flex-none flex-col rounded-md border p-3 sm:flex';
/** `.label-caps` — eyebrow de cada tarjeta. */
const LABEL_CAPS = 'text-[10px] font-semibold uppercase tracking-[0.06em]';

/** La pista que el silo ya guardó. `null` para la derivación externa y para la venta sin decidir. */
function savedTrack(kind: WizardSession['paymentMethodKind']): PaymentTrack | null {
    if (kind === 'SUREKEEP') return 'SUREKEEP';
    if (kind === 'CASH_OR_CARD') return 'CASH';
    return null;
}

/** Lugares para las piezas de Promociones en Forma de pago (REQ-134); sin pieza no pasa nada. */
export interface PaymentMethodSlots {
    /** Se dispara tras derivar una venta con extras, antes de ir al cobro. */
    afterReferral?: AfterReferral;
    /** Se llama después de guardar la forma de pago con éxito. */
    onSaleChanged?: OnSaleChanged;
    /** Promos que gana o pierde cada opción. */
    paymentOption?: (ctx: PaymentOptionContext) => ReactNode;
    /** Monto que paga en esa forma de pago; sin pieza, el total de contado. */
    trackTotal?: (ctx: PaymentOptionContext) => ReactNode;
    /** Total con el que cotiza SureKeep: sin lo que SureKeep no admite (p. ej. solo contado). */
    sureKeepTotalCents?: (session: WizardSession, saleTotalCents: number) => number;
    /** Nombre del regalo en el resumen. */
    giftName?: LineSlots['giftName'];
    /** Nombre de la promo del equipo en el resumen; sin pieza, «Promoción». */
    promotionLabel?: LineSlots['promotionLabel'];
}

export interface PaymentOptionContext {
    track: 'SUREKEEP' | 'CASH' | 'EXTERNAL';
    session: WizardSession;
    /** Total de la venta con lo que tiene apartado ahora. */
    saleTotalCents: number;
}

export function PaymentMethodStep({
    sessionId,
    slots = {},
}: {
    sessionId: string;
    slots?: PaymentMethodSlots;
}) {
    const { data: session } = useWizardSession(sessionId);
    const lineInfo = useCatalogLineInfo(sessionId);
    const rawCatalog = useCatalog(sessionId, { cached: true }).data;
    const {
        data: financiers = [],
        error: financiersError,
        isFetching: financiersFetching,
        refetch: retryFinanciers,
    } = useFinanciers(sessionId);
    const referral = useReferralFlow(sessionId, slots.afterReferral);
    /**
     * `undefined` = el vendedor todavía no tocó nada en esta visita → manda lo que dice la sesión.
     * Una elección suya (incluso la misma) pasa a mandar sobre lo guardado.
     */
    const [touched, setTouched] = useState<PaymentTrack | undefined>(undefined);
    const paymentMethod = usePaymentMethodChange(sessionId, {
        onSaleChanged: slots.onSaleChanged,
        onCashRecovery: () => {
            setTouched('CASH');
        },
    });
    const { goTo, dropped } = paymentMethod;
    const [referProvider, setReferProvider] = useState<FinancierOption | null>(null);
    // «Continuar» gira de principio a fin: guardar, ajustar promos y cambiar de pantalla.
    const [continuing, setContinuing] = useState(false);
    const summary = useMemo(
        () =>
            session?.reservedLines !== undefined
                ? saleSummaryOf(session, lineInfo.descriptions)
                : undefined,
        [session, lineInfo.descriptions],
    );

    if (!session) return <Loading size="lg" />;
    const anchorUnfinanceable = isAnchorUnfinanceable(session);
    // Con crédito en curso en otra sucursal solo queda Contado: ni SureKeep ni derivar.
    const cashOnly = session.cashOnly === true;
    const onlyCash = cashOnly;
    const wanted = onlyCash ? 'CASH' : (touched ?? savedTrack(session.paymentMethodKind));
    // Sin nivel SureKeep la card ni se pinta.
    const sureKeep = isSureKeepAvailable(session);
    // SureKeep viene del endpoint (INTERNAL); si el GET aún no llega, la card se pinta igual con
    // el nombre propio.
    const surekeep = financiers.find((f) => f.type === 'INTERNAL');
    const sureKeepIneligibleReason = sureKeepIneligibleCopy(surekeep);
    // SureKeep no se ofrece si no financia el equipo, aunque la sesión lo tenga guardado.
    const sureKeepBlocked =
        anchorUnfinanceable || !sureKeep || sureKeepIneligibleReason !== undefined;
    const selected = sureKeepBlocked && wanted === 'SUREKEEP' ? null : wanted;
    const product = session.product;
    const hasLines = session.reservedLines !== undefined;
    const financeable = financeableCartLinesOf(
        session,
        lineInfo.serviceSkus,
        lineInfo.descriptions,
    );
    const nothingFinanceable = hasLines && financeable.length === 0;
    const saleTotalCents = cashChargeCents(session);
    const extras = summary?.extras ?? [];
    const catalog = catalogForSession(rawCatalog, sureKeep);
    const split =
        summary &&
        sureKeepSplitOf(
            session,
            summary.totalCents,
            catalog,
            Math.max(
                0,
                (slots.sureKeepTotalCents?.(session, summary.totalCents) ?? summary.totalCents) -
                    summary.totalCents,
            ),
        );
    const anchor = anchorLineOf(session);
    const kitPieces =
        anchor && anchor.components.length > 0
            ? kitPieceNamesOf(anchor, catalog?.items ?? [], descriptionsBySku(catalog?.items ?? []))
            : [];

    const externals = financiers.filter((f) => f.type === 'EXTERNAL' && f.eligible);

    const level = session.loan?.level;
    const notEligible =
        onlyCash ||
        sureKeepIneligibleReason !== undefined ||
        nothingFinanceable ||
        anchorUnfinanceable;
    const referred = isReferredWithExtras(session);
    const referredName = externalProviderName(session.externalProvider, financiers);

    const continuar = async (): Promise<void> => {
        setContinuing(true);
        try {
            await advance();
        } finally {
            setContinuing(false);
        }
    };

    const advance = async (): Promise<void> => {
        paymentMethod.clearDropped();
        if (referred) {
            await goTo('payment');
            return;
        }
        if (!selected) return;
        await paymentMethod.change(selected, () => {
            void continuar();
        });
    };

    const chooseSureKeep = (): void => {
        if (notEligible || continuing) return;
        setTouched('SUREKEEP');
        paymentMethod.clearDropped();
    };

    const onSureKeepKey = (e: KeyboardEvent<HTMLDivElement>): void => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        chooseSureKeep();
    };

    const confirmReferral = async (): Promise<void> => {
        if (!referProvider) return;
        if (await referral.refer(referProvider)) setReferProvider(null);
    };

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Selección"
                title="¿Cómo va a pagar?"
                help={<HowItWorksButton content={PAYMENT_METHOD_HOW_IT_WORKS} />}
                sub={
                    !hasLines && !product ? (
                        <span className="text-destructive">Falta elegir el teléfono.</span>
                    ) : undefined
                }
            />

            {!referred && summary && (
                <div className="border-b border-border bg-muted/40 px-4 py-3">
                    <BuildSaleSummaryPanel
                        summary={summary}
                        readOnly
                        showFinancing={false}
                        variants={lineVariantsBySku(session, catalog?.items)}
                        kitPieces={kitPieces}
                        giftName={slots.giftName}
                        promotionLabel={slots.promotionLabel}
                    />
                </div>
            )}

            {referred ? (
                <WizardCardBody className="flex flex-col gap-4">
                    <p
                        data-testid="referred-sale-notice"
                        className="rounded-md border border-info-soft-border bg-info-soft px-3 py-2 text-[12.5px] text-info"
                    >
                        La venta pasó a {referredName}. Cobra los extras de contado.
                    </p>
                    {extras.length > 0 && <ReferralExtrasNotice extras={extras} />}
                </WizardCardBody>
            ) : (
                <WizardCardBody className="flex flex-col gap-4">
                    {dropped && (
                        <DroppedPromotionsNotice
                            dropped={dropped}
                            onContinue={() => void goTo(dropped.next)}
                        />
                    )}
                    {/* SureKeep — recomendada (INTERNAL del catálogo). Seleccionable, 2 fases. */}
                    {sureKeep && (
                        <div className="relative pt-2">
                            {/* Una opción deshabilitada nunca se marca como recomendada. */}
                            {!notEligible && (
                                <span className="absolute -top-1 left-3.5 z-10 rounded-full bg-success px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-success-foreground">
                                    Recomendada
                                </span>
                            )}
                            <div
                                role="button"
                                tabIndex={notEligible ? -1 : 0}
                                aria-pressed={selected === 'SUREKEEP'}
                                aria-disabled={notEligible || continuing}
                                onClick={chooseSureKeep}
                                onKeyDown={onSureKeepKey}
                                className={cn(
                                    PAY_CARD,
                                    'cursor-pointer',
                                    selected === 'SUREKEEP'
                                        ? 'border-success shadow-[0_0_0_3px_hsl(var(--success)/0.15)]'
                                        : 'hover:border-success/50',
                                    notEligible &&
                                        'cursor-not-allowed opacity-60 hover:border-border',
                                )}
                            >
                                <div className="flex min-w-0 flex-1 flex-col gap-2">
                                    <div className={CARD_BUTTON}>
                                        <span
                                            className={cn(
                                                PAY_ICON,
                                                'bg-success text-success-foreground',
                                            )}
                                        >
                                            <ShieldCheck className="size-[22px]" aria-hidden />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className={cn(LABEL_CAPS, 'text-success')}>
                                                {surekeep?.label ?? 'Nuestra financiera'}
                                            </div>
                                            <div className="mt-0.5 text-[16px] font-semibold leading-tight">
                                                {surekeep?.name ?? 'SureKeep'}
                                            </div>
                                            <p className="mt-1 text-[12px] leading-tight text-muted-foreground">
                                                Pagos semanales.
                                            </p>
                                            {/* Sin nivel previo pide revisar el historial; con nivel no se anticipa nada. */}
                                            {cashOnly ? (
                                                <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-tight text-destructive">
                                                    {CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE}
                                                </div>
                                            ) : sureKeepIneligibleReason !== undefined ? (
                                                <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-tight text-destructive">
                                                    {sureKeepIneligibleReason}
                                                </div>
                                            ) : nothingFinanceable ? (
                                                <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-tight text-destructive">
                                                    Ningún producto de la venta se puede financiar
                                                    con SureKeep. Cóbrala de contado.
                                                </div>
                                            ) : anchorUnfinanceable ? (
                                                <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-tight text-destructive">
                                                    SureKeep no financia este equipo.
                                                </div>
                                            ) : level !== undefined ? null : (
                                                <div className="mt-2 rounded-md border border-warning-soft-border bg-warning-soft p-2 text-[11px] leading-tight text-warning">
                                                    Falta revisar su historial.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {slots.paymentOption && !notEligible && (
                                        <div className={OPTION_PROMOS}>
                                            {slots.paymentOption({
                                                track: 'SUREKEEP',
                                                session,
                                                saleTotalCents,
                                            })}
                                        </div>
                                    )}
                                </div>
                                {split && !notEligible && (
                                    <div
                                        data-testid="surekeep-split"
                                        className={cn(
                                            AMOUNT_BOX,
                                            'gap-2 border-success/30 bg-success/5',
                                        )}
                                    >
                                        <div>
                                            <div className={cn(LABEL_CAPS, 'text-success')}>
                                                A crédito
                                            </div>
                                            <div className="mt-0.5 text-[18px] font-bold tabular-nums text-success">
                                                {formatMXN(split.creditCents / 100)}
                                            </div>
                                        </div>
                                        <div className="border-t border-success/20" />
                                        <div>
                                            <div
                                                className={cn(LABEL_CAPS, 'text-muted-foreground')}
                                            >
                                                Enganche hoy
                                            </div>
                                            <div className="mt-0.5 text-[16px] font-semibold tabular-nums">
                                                {formatMXN(split.downPaymentTodayCents / 100)}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Contado */}
                    <div
                        className={cn(
                            PAY_CARD,
                            selected === 'CASH'
                                ? 'border-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.1)]'
                                : 'hover:border-primary/50',
                        )}
                    >
                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                            <button
                                type="button"
                                disabled={continuing}
                                aria-pressed={selected === 'CASH'}
                                onClick={() => {
                                    setTouched('CASH');
                                    paymentMethod.clearDropped();
                                }}
                                className={CARD_BUTTON}
                            >
                                <span className={cn(PAY_ICON, 'bg-muted text-foreground/70')}>
                                    <CreditCard className="size-[22px]" aria-hidden />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className={cn(LABEL_CAPS, 'text-muted-foreground')}>
                                        Pago único
                                    </div>
                                    <div className="mt-0.5 text-[16px] font-semibold leading-tight">
                                        Contado
                                    </div>
                                    <p className="mt-1 text-[12px] leading-tight text-muted-foreground">
                                        Efectivo, tarjeta o SPEI. Sin pagos después.
                                    </p>
                                </div>
                            </button>
                            {slots.paymentOption && (
                                <div className={OPTION_PROMOS}>
                                    {slots.paymentOption({
                                        track: 'CASH',
                                        session,
                                        saleTotalCents,
                                    })}
                                </div>
                            )}
                        </div>
                        {(hasLines || product) && (
                            <div
                                data-testid="cash-amount"
                                className={cn(
                                    AMOUNT_BOX,
                                    'justify-center border-border bg-muted/30',
                                )}
                            >
                                <div className={cn(LABEL_CAPS, 'text-muted-foreground')}>
                                    A pagar
                                </div>
                                <div className="mt-0.5 text-[18px] font-bold tabular-nums">
                                    {slots.trackTotal
                                        ? slots.trackTotal({
                                              track: 'CASH',
                                              session,
                                              saleTotalCents,
                                          })
                                        : formatMXN(saleTotalCents / 100)}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Otras financieras (EXTERNAL elegibles del endpoint) */}
                    <div>
                        <div className={cn(LABEL_CAPS, 'mb-2 text-muted-foreground')}>
                            Otras financieras que dan crédito para este equipo
                        </div>
                        {/* Cero externas por un 500 se lee igual que un producto que solo va con
                        SureKeep, y esa frase le quita al cliente una opción que sí existe. */}
                        {financiersError ? (
                            <InlineQueryError
                                className="py-2 text-[11.5px]"
                                message={`No se pudieron cargar las otras financieras. ${wizardErrorMsg(financiersError, 'Revisa la conexión.')}`}
                                onRetry={() => void retryFinanciers()}
                                retrying={financiersFetching}
                            />
                        ) : externals.length > 0 ? (
                            <>
                                <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]">
                                    {externals.map((provider) => (
                                        <button
                                            key={provider.code}
                                            type="button"
                                            disabled={onlyCash}
                                            onClick={() => {
                                                setReferProvider(provider);
                                            }}
                                            className={cn(
                                                PAY_CARD,
                                                'items-center',
                                                onlyCash
                                                    ? 'cursor-not-allowed opacity-60'
                                                    : 'hover:border-primary/50',
                                            )}
                                        >
                                            <span
                                                className={cn(PAY_ICON, 'bg-info-soft text-info')}
                                            >
                                                <ExternalLink className="size-5" aria-hidden />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[14px] font-semibold leading-tight">
                                                    {provider.name}
                                                </div>
                                                {onlyCash && (
                                                    <p className="mt-1 text-[11px] leading-tight text-destructive">
                                                        {CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE}
                                                    </p>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="py-2 text-[11.5px] text-muted-foreground">
                                {cashOnly
                                    ? CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE
                                    : sureKeep
                                      ? 'Este equipo solo se ofrece con SureKeep. Si el cliente no lo quiere así, tiene que ser Contado.'
                                      : 'Ninguna otra financiera da crédito para este equipo.'}
                            </p>
                        )}
                    </div>
                </WizardCardBody>
            )}

            <WizardFootNav
                back={
                    <WizardBackLink
                        sessionId={sessionId}
                        step={previousStepSlug('payment-method', session) ?? 'build-sale'}
                    />
                }
            >
                <Button
                    size="sm"
                    disabled={!selected && !referred}
                    isLoading={continuing || paymentMethod.busy}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() => void continuar()}
                >
                    Continuar
                </Button>
            </WizardFootNav>

            <ConfirmDialog
                open={referProvider !== null}
                onOpenChange={(open) => {
                    if (!open) setReferProvider(null);
                }}
                title={`Pasar la venta a ${referProvider?.name ?? ''}`}
                description={
                    extras.length > 0
                        ? 'El teléfono pasa a la financiera externa y se libera su apartado. Enseguida cobras aquí los extras.'
                        : 'La venta pasa a la financiera externa. Al regresar el cliente seguimos en Bienvenida. Esto libera el equipo reservado.'
                }
                confirmLabel="Pasar la venta"
                loading={referral.isPending}
                onConfirm={() => void confirmReferral()}
            >
                {extras.length > 0 && <ReferralExtrasNotice extras={extras} />}
            </ConfirmDialog>
        </WizardCard>
    );
}
